import { useState, useEffect, useCallback, useContext } from "react";
import toast from "react-hot-toast";

import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { AuthContext } from "../../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../../shared/context/clinica-context";

/* Lembretes por SMS na lista de marcações da secretária.
 *
 * Existe porque a lista tem dois gémeos — MarcacoesSecretaria (agenda normal) e
 * MarcacoesTratamentoSecretaria (agenda de tratamentos) — e a lógica de envio é
 * a mesma nos dois. Duplicada, divergia: a tradução dos motivos já estava a ser
 * mantida em dois sítios dentro do MESMO ficheiro (envio individual e envio em
 * massa), e o envio em massa ficou para trás — não conhecia os motivos
 * `consulta_ja_passou` nem `demasiado_tarde`, e essas marcações desapareciam
 * sem aviso nenhum para a secretária.
 *
 * Aqui a tradução é uma tabela só, usada pelos dois caminhos.
 *
 * O envio é da secretária e mais ninguém: cada SMS é pago e o paciente recebe-o
 * uma vez. */

/* Lembretes por SMS DESLIGADOS — prontos, mas não entram nesta versão.
 *
 * Vive aqui para os dois ecrãs da secretária lerem o mesmo interruptor: a
 * agenda normal e a de tratamentos. Um `false` num sítio só, em vez de blocos
 * de JSX comentados em dois ficheiros — que é fácil de descomentar pela
 * metade e deixar um ecrã com o botão e o outro sem.
 *
 * Ao religar, confirmar antes: saldo em `creditosms` (a coleção estava
 * vazia), `LINK_APP` no `.env` do servidor, e o `try_files` no nginx para o
 * link de confirmação não dar 404. */
export const SMS_ATIVO = false;

const MOTIVOS = {
  sms_desativado: (n) => `${n}: SMS desactivado na ficha do cliente`,
  sem_numero: (n) => `${n}: sem número de contacto`,
  numero_estrangeiro: (n) => `${n}: número estrangeiro, SMS não enviado`,
  numero_fixo: (n) => `${n}: número fixo, SMS não enviado`,
  prefixo_desconhecido: (n) => `${n}: número não reconhecido`,
  consulta_ja_passou: (n) => `${n}: a consulta já passou`,
  demasiado_tarde: (n) =>
    `${n}: falta menos de uma hora — telefone em vez de enviar`,
  saldo_insuficiente: () => "Saldo insuficiente para continuar a enviar SMS.",
};

/* De que colecção é a marcação — é o que o servidor usa para a ir buscar.
 *
 * `isTratamento` tem de vir de fora: numa clínica configurada com agenda de
 * TRATAMENTOS as marcações vivem na `AgendaTratamento`, não na `Agenda`. Com o
 * tipo errado o servidor procura na colecção errada, não encontra nada e
 * responde 404. */
const tipoDe = (row, isTratamento) => {
  if (row.tratamentoIsolado) return "TratamentoIsolado";
  return isTratamento ? "AgendaTratamento" : "Agenda";
};

export function useSmsLembretes(marcacoes, isTratamento = false) {
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const clinicaId = clinica?.clinica?._id;

  const [estadosSms, setEstadosSms] = useState({}); // { [id]: { enviado, confirmado } }
  const [enviandoSmsId, setEnviandoSmsId] = useState(null);
  const [enviandoSmsTodos, setEnviandoSmsTodos] = useState(false);
  const [confirmacaoSmsDialogOpen, setConfirmacaoSmsDialogOpen] =
    useState(false);

  // Quais já foram enviados — para a coluna e para não repetir no envio em massa.
  useEffect(() => {
    // Desligado: não vale a pena perguntar ao servidor estados que ninguém vê.
    if (!SMS_ATIVO) return;
    if (!marcacoes || marcacoes.length === 0) return;

    const fetchEstadosSms = async () => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/sms/lembretes/estado/bulk`,
          "POST",
          JSON.stringify({ ids: marcacoes.map((m) => m.id) }),
          {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
          },
        );
        setEstadosSms(response || {});
      } catch (err) {
        console.error("Erro ao carregar estados SMS:", err);
      }
    };

    fetchEstadosSms();
  }, [marcacoes, auth.token, sendRequest]);

  /* `silencioso` cala os avisos de sucesso no envio em massa — trinta pacientes
   * dariam trinta avisos. Os de recusa aparecem sempre: são por paciente e a
   * secretária tem de saber quem ficou por avisar. */
  const enviarUm = useCallback(
    async (row, silencioso = false) => {
      if (!silencioso) setEnviandoSmsId(row.id);
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/sms/lembretes/individual/${clinicaId}`,
          "POST",
          JSON.stringify({
            agendamentoId: row.id,
            tipoAgendamento: tipoDe(row, isTratamento),
          }),
          {
            Authorization: "Bearer " + auth.token,
            "Content-Type": "application/json",
          },
        );

        if (response?.ignorado) {
          const mensagem = MOTIVOS[response.motivo]?.(row.nome);
          if (mensagem) toast.error(mensagem);
          return { ok: false, motivo: response.motivo };
        }

        setEstadosSms((prev) => ({
          ...prev,
          [row.id]: { enviado: true, confirmado: false },
        }));
        if (!silencioso) toast.success(`SMS enviado para ${row.nome}`);
        return { ok: true };
      } catch (err) {
        /* O servidor já distingue «sem ligação à operadora» de «a operadora
         * recusou», e diz porquê. Mostra-se isso em vez de um «erro» genérico:
         * sem ligação vê-se a rede e o IP autorizado, recusada vê-se o número
         * ou a conta. */
        if (!silencioso) {
          toast.error(`${row.nome}: ${err?.message || "erro ao enviar SMS"}`);
        }
        return { ok: false, motivo: "erro" };
      } finally {
        if (!silencioso) setEnviandoSmsId(null);
      }
    },
    [sendRequest, auth.token, clinicaId, isTratamento],
  );

  const pendentes = (marcacoes || []).filter(
    (row) => !row.atendido && !row.faltou && !estadosSms[row.id]?.enviado,
  );

  const enviarTodos = useCallback(async () => {
    setConfirmacaoSmsDialogOpen(false);
    setEnviandoSmsTodos(true);
    let enviados = 0;
    let erros = 0;
    // Quem falhou, e não só quantos: numa lista de trinta pacientes, saber que
    // falharam três sem saber quais é quase o mesmo que não saber nada.
    const falhados = [];

    try {
      for (const row of pendentes) {
        // Um segundo entre envios: a operadora recusa rajadas.
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const resultado = await enviarUm(row, true);
        if (resultado.ok) enviados++;
        // Sem saldo não vale a pena continuar — as seguintes falhariam todas.
        else if (resultado.motivo === "saldo_insuficiente") break;
        else if (resultado.motivo === "erro") {
          erros++;
          falhados.push(row.nome);
        }
      }

      if (enviados > 0) toast.success(`${enviados} SMS enviados com sucesso.`);
      if (erros > 0) {
        toast.error(`${erros} SMS falharam: ${falhados.join(", ")}`, {
          duration: 10000,
        });
      }
      if (enviados === 0 && erros === 0 && pendentes.length === 0) {
        toast("Todos os agendamentos já foram notificados.");
      }
    } finally {
      setEnviandoSmsTodos(false);
    }
  }, [pendentes, enviarUm]);

  return {
    estadosSms,
    enviandoSmsId,
    enviandoSmsTodos,
    enviarSmsIndividual: enviarUm,
    enviarSmsTodos: enviarTodos,
    confirmacaoSmsDialogOpen,
    setConfirmacaoSmsDialogOpen,
    abrirConfirmacaoSmsTodos: () => setConfirmacaoSmsDialogOpen(true),
    pendentesCount: pendentes.length,
  };
}
